'use client';
import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import CityAutocomplete from '@/components/ui/CityAutocomplete';

const today = () => new Date().toISOString().split('T')[0];

interface Props {
  open: boolean;
  onClose: () => void;
  city: string;
  onCityChange: (v: string) => void;
  date: string;
  onDateChange: (v: string) => void;
  ratingMin: string;
  onRatingMinChange: (v: string) => void;
  priceMin: string;
  onPriceMinChange: (v: string) => void;
  priceMax: string;
  onPriceMaxChange: (v: string) => void;
  withoutSchedule: boolean;
  onWithoutScheduleChange: (v: boolean) => void;
  onReset: () => void;
}

const ratingOptions: { value: string; label: string }[] = [
  { value: '', label: 'Любой' },
  { value: '3', label: '3+ ★' },
  { value: '4', label: '4+ ★' },
  { value: '4.5', label: '4.5+ ★' },
];

export default function FiltersSheet({
  open, onClose,
  city, onCityChange,
  date, onDateChange,
  ratingMin, onRatingMinChange,
  priceMin, onPriceMinChange,
  priceMax, onPriceMaxChange,
  withoutSchedule, onWithoutScheduleChange,
  onReset,
}: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) onClose();
    };
    const escHandler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', escHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', escHandler);
    };
  }, [open, onClose]);

  // Блокировка скролла фона, пока открыт мобильный bottom-sheet
  useEffect(() => {
    if (!open || window.innerWidth >= 640) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const ratingChipClass = (active: boolean) =>
    `px-3.5 py-2.5 rounded-full text-sm font-medium border transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${
      active ? 'bg-brand text-white border-brand' : 'bg-brand-muted text-brand border-transparent hover:bg-brand/10'
    }`;

  const Body = (
    <div className="flex flex-col gap-4">
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1.5">Город</label>
        <CityAutocomplete value={city} onChange={onCityChange} placeholder="Город" className="w-full" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1.5">Дата</label>
        <input
          type="date"
          value={date}
          min={today()}
          onChange={e => onDateChange(e.target.value)}
          className="w-full border border-border-input rounded-xl px-4 py-2.5 text-sm text-text-body focus:border-brand focus:ring-2 focus:ring-brand/20 transition-all"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1.5">Рейтинг от</label>
        <div className="flex flex-wrap gap-2">
          {ratingOptions.map(opt => (
            <button
              key={opt.value || 'any'}
              type="button"
              aria-pressed={ratingMin === opt.value}
              onClick={() => onRatingMinChange(opt.value)}
              className={ratingChipClass(ratingMin === opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <div className="flex-1 min-w-0">
          <label className="block text-xs font-medium text-gray-500 mb-1.5">Цена от</label>
          <input
            type="number" value={priceMin} onChange={e => onPriceMinChange(e.target.value)}
            placeholder="0" min="0"
            className="w-full border border-border-input rounded-xl px-3 py-2.5 text-sm text-text-body focus:border-brand focus:ring-2 focus:ring-brand/20 transition-all"
          />
        </div>
        <div className="flex-1 min-w-0">
          <label className="block text-xs font-medium text-gray-500 mb-1.5">до</label>
          <input
            type="number" value={priceMax} onChange={e => onPriceMaxChange(e.target.value)}
            placeholder="∞" min="0"
            className="w-full border border-border-input rounded-xl px-3 py-2.5 text-sm text-text-body focus:border-brand focus:ring-2 focus:ring-brand/20 transition-all"
          />
        </div>
      </div>
      <label className="flex items-center gap-2 cursor-pointer select-none py-1">
        <input
          type="checkbox"
          checked={withoutSchedule}
          onChange={e => onWithoutScheduleChange(e.target.checked)}
          className="accent-brand w-4 h-4"
        />
        <span className="text-sm text-text-secondary">Показать профи без расписания</span>
      </label>
      <button
        type="button"
        onClick={onReset}
        className="text-sm font-medium text-brand hover:underline self-start focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 rounded"
      >
        Сбросить всё
      </button>
    </div>
  );

  return (
    <div ref={wrapperRef}>
      {/* Мобильный bottom-sheet */}
      <div className="sm:hidden fixed inset-0 z-40 bg-black/30" aria-hidden="true" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Фильтры"
        className="sm:hidden fixed inset-x-0 bottom-0 z-40 bg-bg-surface rounded-t-2xl shadow-lg max-h-[85vh] overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+1rem)]"
      >
        <div className="flex items-center justify-between px-5 pt-4 pb-2 sticky top-0 bg-bg-surface">
          <h2 className="text-sm font-semibold text-text-primary">Фильтры</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрыть фильтры"
            className="p-2 -m-2 text-gray-500 hover:text-text-secondary rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-5 pb-2">{Body}</div>
      </div>

      {/* Десктоп: popover-панель */}
      <div
        role="dialog"
        aria-label="Фильтры"
        className="hidden sm:block absolute right-0 top-full mt-2 z-40 w-96 max-w-[90vw] bg-bg-surface rounded-2xl border border-border shadow-lg p-5"
      >
        {Body}
      </div>
    </div>
  );
}
